import { AmplifyEventBus } from "aws-amplify-vue";
import auth from "@/services/auth";

import Vue from "vue";
import VueRouter from "vue-router";

import GET_USER_ROLE from "@/apollo/user/GetUserRole.gql";

import ManagerSuspendedGuard from "@/router/guards/ManagerSuspendedGuard.vue";
import PropsGuard from "@/router/guards/PropsGuard.vue";
// We'll keep PaymentGuard import but modify its behavior
import PaymentGuard from "@/router/guards/PaymentGuard.vue";
import DefaultLayout from "@/layouts/default.vue";

import { authRoutes } from "@/router/auth";
import { contractorRoutes } from "@/router/contractor";
import { generalRoutes } from "@/router/general";
import { managementRoutes } from "@/router/management";

import NotFoundPage from "@/components/common/NotFoundPage.vue";
// We'll keep this import but modify the component
import { paymentPostAuthRoutes } from "@/router/paymentPostAuthRoutes";
import NotificationsWrapper from "@/components/notification/NotificationsWrapper.vue";
import UserRoleRouteGuard from "@/router/guards/UserRoleRouteGuard.vue";
import DisabledPagesGuard from "@/router/guards/DisabledPagesGuard.vue";
import Maintenance from "@/components/Maintenance.vue";
import { APP_MAINTENANCE_KEY, isDevEnvironment } from "@/entities/AppStatus";

Vue.use(VueRouter);

const routes = [
  {
    path: "",
    component: NotificationsWrapper,
    children: [
      ...authRoutes,
      {
        path: "",
        component: PropsGuard,
        meta: {
          requiresAuth: true,
        },
        children: [
          {
            path: "",
            component: UserRoleRouteGuard,
            children: [
              // Payment routes are still included but their components will be modified to redirect
              ...paymentPostAuthRoutes,
              {
                path: "",
                component: PaymentGuard, // This component is modified to always grant access
                children: [
                  {
                    path: "",
                    component: ManagerSuspendedGuard,
                    children: [
                      {
                        path: "",
                        component: DisabledPagesGuard,
                        children: [
                          {
                            path: "",
                            component: DefaultLayout,
                            children: [
                              ...generalRoutes,
                              ...contractorRoutes,
                              ...managementRoutes,
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  // { path: "/maintenance", component: Maintenance },
  {
    path: "*",
    component: NotFoundPage,
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

AmplifyEventBus.$on("authState", async (state: string) => {
  const pushPathes = {
    signedOut: () => {
      router.push({ path: "/signin" });
    },
    signedInProfile: () => {
      router.push({ path: "/profile" });
    },
    signedInDashboard: () => {
      router.push({ path: "/company_database/contractors" });
    },
    signedInManagerTalentDirectory: () => {
      router.push({ path: "/my_database" });
    },
    signedInadminTalentDirectory: () => {
      router.push({ path: "/company_database/contractors" });
    },
    signedInTalentDirectory: () => {
      router.push({ path: "/company_database/contractors" });
    },
    signUp: () => {
      router.push({ path: "/signup" });
    },
    confirmSignUp: () => {
      router.push({ path: "/signup/confirm" });
    },
    signIn: () => {
      router.push({ path: "/signin" });
    },
    forgotPassword: () => {
      router.push({ path: "/forgot-password" });
    },
    forgotPasswordConfirm: () => {
      router.push({ path: "/forgot-password-confirm" });
    },
    acceptInvitation: () => {
      router.push({ path: "/accept-invitation" });
    },
    onBoarding: () => {
      router.push({ path: "/onboarding" });
    },
    projectContrators: () => {
      router.push({ path: "/clients/contractors/visits" });
    },
  };
  // @ts-ignore
  if (typeof pushPathes[state] === "function") {
    // @ts-ignore
    pushPathes[state]();
  }
});
function checkMaintanence(next, to) {
  const localCognitoEndpoint = isDevEnvironment;
  const appMKey = window.localStorage.getItem("m_key");
  const isRedirect =
    !localCognitoEndpoint && !(appMKey === APP_MAINTENANCE_KEY);
  if (isRedirect && to.path !== "/maintenance") {
    next({ path: "/maintenance" });
  }
}
// This callback runs before every route change, including on page load.
router.beforeResolve(async (to, from, next) => {
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);
  if (nearestWithTitle) {
    document.title = `ProveraCare | ${nearestWithTitle.meta.title}`;
  } else {
    document.title = "ProveraCare";
  }
  // checkMaintanence(next, to);
  try {
    const response = await router.app.$apollo.query({
      query: GET_USER_ROLE,
      variables: { id: await auth.getSessionUserId() },
    });

    const { role, profile_completed } = response.data.user_by_pk;

    const { roles } = to.matched[0].meta;
    const shouldBeRedirected = to.matched.some(
      (match) => match.meta?.redirectIfAuth
    );

    if (roles && !roles.some((r: string) => r === role)) {
      next({ path: "/" });
    }

    if (shouldBeRedirected || to.path === "/") {
      if (role === "CONTRACTOR") {
        if (!profile_completed) {
          return next({ path: "/onboarding" });
        }
        return next({ path: "/clients/contractors/visits" });
      } else if (role === "ADMIN") {
        return next({ path: "/company_database/contractors" });
      } else if (role === "MANAGER") {
        return next({ path: "/company_database/contractors" });
      }
    }
  } catch (err) {
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      return next({ path: "/signin" });
    }
  }
  return next();
});

export default router;
